import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TrendingUp, ShieldAlert, FlaskConical, ArrowRight, Calculator as CalcIcon, Zap } from "lucide-react";
import { fmtCurrency, fmtPerPulse, costForShots, costForShotsBreakdown, BREAKDOWN_COLORS } from "@/lib/calc";

const BREAKDOWN_ITEMS = [
  { key: "flashlamp", label: "Flashlamp Servicing", color: BREAKDOWN_COLORS.flashlamp },
  { key: "consumables", label: "Consumables", color: BREAKDOWN_COLORS.sundries },
];

const StatCard = ({ icon: Icon, label, value, sub, testId, accent }) => (
  <Card className="rounded-2xl border bg-card p-5 shadow-sm">
    <div className="flex items-center gap-2 text-muted-foreground mb-2">
      <Icon className="h-4 w-4" style={{ color: accent }} />
      <span className="font-mono-label text-[11px] uppercase tracking-wider">{label}</span>
    </div>
    <div className="font-heading font-bold text-2xl sm:text-3xl tabular-nums" data-testid={testId}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </Card>
);

const ShotCostLookup = ({ result, currency }) => {
  const [shots, setShots] = useState(1000);
  const total = costForShots(result, shots);
  const bd = costForShotsBreakdown(result, shots);
  const rows = [
    { key: "flashlamp", label: "Flashlamp servicing", value: bd.flashlamp },
    { key: "consumables", label: "Consumables", value: bd.consumables },
    { key: "electricity", label: "Electricity (firing time)", value: bd.electricity },
    { key: "reserve", label: "Major-failure reserve", value: bd.reserve },
  ].filter((r) => r.value > 0);
  return (
    <Card className="rounded-2xl border bg-card p-5 shadow-sm" data-testid="shots-lookup">
      <div className="flex items-center gap-2 mb-1">
        <CalcIcon className="h-4 w-4 text-primary" />
        <h3 className="font-heading font-semibold text-base">Cost for a number of shots</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Fully-loaded machine cost — flashlamp servicing, consumables, electricity (by firing time) and the major-failure reserve. To price full treatments with staff time and products, use the <Link to="/treatments" className="text-primary hover:underline">Treatment Builder</Link>.</p>
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Shots</label>
          <Input type="number" min="0" value={shots} onChange={(e) => setShots(e.target.value)} className="h-11 rounded-lg tabular-nums mt-1" data-testid="lookup-shots" />
        </div>
        <div className="text-right">
          <div className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Fully-loaded cost</div>
          <div className="font-heading font-extrabold text-3xl tabular-nums text-primary" data-testid="lookup-total">{fmtCurrency(total, currency)}</div>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border space-y-1.5" data-testid="lookup-breakdown">
          {rows.map((r) => (
            <div key={r.key} className="flex justify-between text-sm" data-testid={`lookup-bd-${r.key}`}>
              <span className="text-muted-foreground">{r.label}</span>
              <span className="tabular-nums">{fmtCurrency(r.value, currency)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export const Results = ({ result, currency }) => {
  const chartData = BREAKDOWN_ITEMS.map((it) => ({ name: it.label, value: result.breakdownPerPulse[it.key], color: it.color }))
    .filter((d) => d.value > 0);

  return (
    <div className="space-y-4">
      {/* Hero cost-per-shot */}
      <Card className="rounded-2xl border bg-card p-6 shadow-sm relative overflow-hidden">
        <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
        <span className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Flashlamp cost per shot</span>
        <div className="flex items-end gap-3 mt-1">
          <div className="font-heading font-extrabold text-4xl sm:text-5xl tabular-nums text-primary" data-testid="output-cost-per-pulse">{fmtPerPulse(result.totalPerPulse, currency)}</div>
          <span className="text-sm text-muted-foreground mb-1.5">/ shot</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 items-center">
          <div className="flex justify-center">
            <PieChart width={190} height={180}>
              <Pie data={chartData.length ? chartData : [{ name: "—", value: 1, color: "#e5e7eb" }]} dataKey="value" cx={95} cy={90} innerRadius={52} outerRadius={82} paddingAngle={2} stroke="none" isAnimationActive={false}>
                {(chartData.length ? chartData : [{ color: "#e5e7eb" }]).map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtPerPulse(v, currency)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))", fontSize: 12 }} />
            </PieChart>
          </div>
          <div className="space-y-2.5">
            {BREAKDOWN_ITEMS.map((it) => (
              <div key={it.key} className="flex items-center justify-between text-sm" data-testid={`breakdown-${it.key}`}>
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: it.color }} />
                  <span className="text-muted-foreground">{it.label}</span>
                </div>
                <span className="font-medium tabular-nums">{fmtPerPulse(result.breakdownPerPulse[it.key], currency)}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={Zap} label="Electricity / hour" accent="#F59E0B" testId="output-elec-per-hour"
          value={fmtCurrency(result.elecPerHour, currency, 2)} sub="Machine kW × your electricity price — costed by treatment time" />
        <StatCard icon={TrendingUp} label="Flashlamp per 1,000 shots" accent="#38BDF8" testId="output-cost-per-1000"
          value={fmtCurrency(result.totalPerPulse * 1000, currency)} sub="Flashlamp wear (+ any per-shot consumables)" />
        <StatCard icon={ShieldAlert} label="Major Failure Reserve" accent="#F43F5E" testId="output-failure-reserve"
          value={fmtCurrency(result.annualReserve, currency) + " /yr"}
          sub={result.annualReserve > 0 ? `${fmtCurrency(result.monthlyReserve, currency)} / month set aside` : "Covered by warranty — no reserve"} />
      </div>

      <ShotCostLookup result={result} currency={currency} />

      {/* Full-treatment pricing callout */}
      <Card className="rounded-2xl border bg-card p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <FlaskConical className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-base">Need the price to charge a client?</h3>
            <p className="text-sm text-muted-foreground mt-1">
              This is the machine running cost only. A treatment's true cost also needs
              <strong className="text-foreground"> staff time (wages)</strong> and materials — priced accurately in the Treatment Builder.
            </p>
            <Link to="/treatments" data-testid="cta-treatment-builder">
              <Button className="rounded-lg mt-3" size="sm">Price a full treatment <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
};
